import React, {MutableRefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flex, mt, p, pr} from "@pg-design/helpers-css-strings";
import {BellIcon, FiltersIcon, MapIcon, SearchIcon, SortIcon, TilesIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";

import {IRPStore} from "../../app/rp_reducer";
import {OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {generatePillLabel} from "../utils/generate_pill_label";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    selectedRegionsCount?: number;
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: MutableRefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    isMap?: boolean;
    sortActive?: boolean;
    isAbroad?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
}

const mobileSearchTriggersWrapperHeight = 64;

export const MobileTopSearchTriggers = (props: IProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const offersCount = useSelector((state: IRPStore) => state.offerList.list.count);
    const offersMapCount = useSelector((state: IRPStore) => state.offerListMap.list.count);

    const searchTriggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        props.onMount && props.onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        props.onToggleSortClick && props.onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionPillLabel = useMemo(() => {
        if (!props.searchFieldPropsValue) {
            return props.firstSelectedRegion || null;
        }

        return generatePillLabel(false, props.searchFieldPropsValue);
    }, [props.searchFieldPropsValue]);

    return (
        <div css={wrapper}>
            <div css={triggersHolder}>
                {!props.isAbroad && (
                    <div css={inputWrapper} onClick={props.onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {props.firstSelectedRegion ? (
                            <Pill size="small">
                                <Text noWrap css={[inputWrapperText]}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ) : (
                            <Text noWrap css={[inputWrapperText]}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}
                <div css={triggerIconHolder} onClick={props.onToggleFiltersClick}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                    <Text variant="info_txt_2" css={gapTop}>
                        filtruj
                    </Text>
                    {props.selectedFiltersCount ? <div css={triggerBadge}>{props.selectedFiltersCount}</div> : ""}
                </div>
                <div css={triggerIconHolder} onClick={onSortClick}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={props.sortActive ? theme.colors.secondary : theme.colors.gray[600]}
                    />
                    <Text variant="info_txt_2" css={gapTop} color={props.sortActive ? theme.colors.secondary : theme.colors.gray[600]}>
                        sortuj
                    </Text>
                </div>
                {props.latestQuery?.region && !props.isAbroad && (
                    <OfferListNotificationsSubscriptionModal
                        customButton={(props) => (
                            <div css={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                                <Text variant="info_txt_2" css={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>
            {(offersCount > 0 || offersMapCount > 0) && (
                <Button
                    size="x-small"
                    variant="filled_secondary"
                    href={props.isMap ? props.listUrl : props.mapUrl}
                    css={mapButton}
                    iconLeft={props.isMap ? TilesIcon : MapIcon}
                >
                    <Text variant="button_small">{props.isMap ? "Lista" : "Mapa"}</Text>
                </Button>
            )}
        </div>
    );
};

const wrapper = (theme: Theme) => css`
    background: #fff;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const triggersHolder = css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 0 auto;
    gap: ${calculateRemSize(3)};
    width: 100%;
`;

const triggerIconHolder = (theme: Theme) => css`
    color: ${theme.colors.secondary};
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: default;
    &:visited,
    &:hover,
    &:focus,
    &:active {
        color: ${theme.colors.secondary};
        text-decoration: none;
    }
`;

const triggerBadge = (theme: Theme) => css`
    position: absolute;
    top: 0;
    right: 0;
    color: #fff;
    background: ${theme.colors.danger};
    font-size: ${theme.globals.text.root};
    border-radius: 50%;
    border: 1px solid #fff;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateX(50%);
`;

const mapButton = css`
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: ${calculateRemSize(1.5)};
    ${elevation(4)};
    gap: ${calculateRemSize(1)};
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 106px;
    z-index: 1;
`;

const inputWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${elevation(2)};
    border: 1px solid ${theme.colors.gray[600]};
    width: 100%;
    margin-right: -${calculateRemSize(1)};
    ${p(1, 2)};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
`;

const inputWrapperText = css`
    ${pr(1)};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100vw - 261px);
`;

const gapTop = css`
    ${mt(0.5)};
`;
import React, {MutableRefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, flex, mt, p, pr} from "@pg-design/helpers-css-strings";
import {BellIcon, FiltersIcon, MapIcon, SearchIcon, SortIcon, TilesIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";

import {IRPStore} from "../../app/rp_reducer";
import {OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {generatePillLabel} from "../utils/generate_pill_label";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    selectedRegionsCount?: number;
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: MutableRefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    isMap?: boolean;
    sortActive?: boolean;
    isAbroad?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
}

const mobileSearchTriggersWrapperHeight = 64;

export const MobileTopSearchTriggers = (props: IProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const offersCount = useSelector((state: IRPStore) => state.offerList.list.count);
    const offersMapCount = useSelector((state: IRPStore) => state.offerListMap.list.count);

    const searchTriggerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        props.onMount && props.onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        props.onToggleSortClick && props.onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionPillLabel = useMemo(() => {
        if (!props.searchFieldPropsValue) {
            return props.firstSelectedRegion || null;
        }

        return generatePillLabel(false, props.searchFieldPropsValue);
    }, [props.searchFieldPropsValue]);

    return (
        <div css={wrapper}>
            <div css={triggersHolder}>
                {!props.isAbroad && (
                    <div css={inputWrapper} onClick={props.onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {props.firstSelectedRegion ? (
                            <Pill size="small">
                                <Text noWrap css={[inputWrapperText]}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ) : (
                            <Text noWrap css={[inputWrapperText]}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}
                <div css={triggerIconHolder} onClick={props.onToggleFiltersClick}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                    <Text variant="info_txt_2" css={gapTop}>
                        filtruj
                    </Text>
                    {props.selectedFiltersCount ? <div css={triggerBadge}>{props.selectedFiltersCount}</div> : ""}
                </div>
                <div css={triggerIconHolder} onClick={onSortClick}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={props.sortActive ? theme.colors.secondary : theme.colors.gray[600]}
                    />
                    <Text variant="info_txt_2" css={gapTop} color={props.sortActive ? theme.colors.secondary : theme.colors.gray[600]}>
                        sortuj
                    </Text>
                </div>
                {props.latestQuery?.region && !props.isAbroad && (
                    <OfferListNotificationsSubscriptionModal
                        customButton={(props) => (
                            <div css={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />
                                <Text variant="info_txt_2" css={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>
            {(offersCount > 0 || offersMapCount > 0) && (
                <Button
                    size="x-small"
                    variant="filled_secondary"
                    href={props.isMap ? props.listUrl : props.mapUrl}
                    css={mapButton}
                    iconLeft={props.isMap ? TilesIcon : MapIcon}
                >
                    <Text variant="button_small">{props.isMap ? "Lista" : "Mapa"}</Text>
                </Button>
            )}
        </div>
    );
};

const wrapper = (theme: Theme) => css`
    background: #fff;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const triggersHolder = css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin: 0 auto;
    gap: ${calculateRemSize(3)};
    width: 100%;
`;

const triggerIconHolder = (theme: Theme) => css`
    color: ${theme.colors.secondary};
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: default;
    &:visited,
    &:hover,
    &:focus,
    &:active {
        color: ${theme.colors.secondary};
        text-decoration: none;
    }
`;

const triggerBadge = (theme: Theme) => css`
    position: absolute;
    top: 0;
    right: 0;
    color: #fff;
    background: ${theme.colors.danger};
    font-size: ${theme.globals.text.root};
    border-radius: 50%;
    border: 1px solid #fff;
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    justify-content: center;
    align-items: center;
    transform: translateX(50%);
`;

const mapButton = css`
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: ${calculateRemSize(1.5)};
    ${elevation(4)};
    gap: ${calculateRemSize(1)};
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    width: 106px;
    z-index: 1;
`;

const inputWrapper = (theme: Theme) => css`
    ${flex("center")};
    ${elevation(2)};
    border: 1px solid ${theme.colors.gray[600]};
    width: 100%;
    margin-right: -${calculateRemSize(1)};
    ${p(1, 2)};
    gap: ${calculateRemSize(1)};
    ${borderRadius(1)};
`;

const inputWrapperText = css`
    ${pr(1)};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: calc(100vw - 261px);
`;

const gapTop = css`
    ${mt(0.5)};
`;
